/**
 * NUDIENT: Tomt läge på en kollektion med skal, M-146 tillägget.
 *
 * När kundens modell saknar skal i kollektionen nämner texten skaltypen
 * och modellen, och länkar till de skaltyper som har skal för modellen:
 * "We don't have Bold Cases for iPhone Duo yet. See Slice Case for iPhone
 * Duo, or choose another model." Har ingen skaltyp skal för modellen står
 * texten som förut.
 *
 * - Skaltypen är kollektionens namn, ur sections/nudient-collection-grid.liquid.
 * - Modellen är filtrets värde i adressen. Shopifys filter listar bara
 *   modeller som har produkter i kollektionen, så Liquid ser den inte.
 * - Skaltyperna är undernavigeringens länkar, a[data-nudient-category-link],
 *   med menyns översatta namn, assets/nudient-category-nav.js. Varje skaltyp
 *   hämtas en gång med modellen i filtret och samma sektion, Shopifys Section
 *   Rendering API, och räknas som en skaltyp med skal när rutnätet har kort.
 *   Bara i tomt läge, alla på en gång.
 * - Länken får modellen i filtret, så att kunden ser skalen för modellen.
 * - Flera skaltyper binds ihop med webbläsarens Intl.ListFormat på sidans
 *   språk, "Thin Cases or Slice Case".
 *
 * Elementet är ett eget element, så att det körs också när Horizon ritar om
 * rutnätet efter ett filterbyte.
 *
 * Värden som inte kommer från butiken eller en token:
 * - [type], [model] och [types]: platserna i texterna nudient.filter.no_type
 *   och nudient.filter.see_types.
 * - filter.p.m.attribute.compatible_models: filtrets parameter, M-013.
 * - section_id och shopify-section-: Shopifys parameter och sektionens id.
 * - nudient-collection-grid__item: kortens klass i rutnätet.
 * - disjunction: Intl.ListFormat för "eller".
 */

const PARAM = 'filter.p.m.attribute.compatible_models';

class NudientEmptyTypes extends HTMLElement {
  connectedCallback() {
    this.#build();
  }

  async #build() {
    const text = this.closest('.nudient-filter-empty')?.querySelector('.nudient-filter-empty__text');
    const section = this.closest('[id^="shopify-section-"]');
    const model = new URL(window.location.href).searchParams.get(PARAM);
    if (!text || !section || !model) return;
    const { type = '', lead = '', see = '', gap = ' ' } = this.dataset;
    if (!lead || !see) return;
    const sectionId = section.id.replace(/^shopify-section-/, '');
    const here = window.location.pathname;

    const candidates = [];
    const seen = new Set();
    document.querySelectorAll('a[data-nudient-category-link]').forEach((link) => {
      if (!(link instanceof HTMLAnchorElement)) return;
      const path = new URL(link.href, window.location.origin).pathname;
      if (path === here || seen.has(path)) return;
      seen.add(path);
      candidates.push({ path, name: link.textContent.trim() });
    });
    if (!candidates.length) return;

    const checked = await Promise.all(
      candidates.map(async (candidate) => {
        const url = new URL(candidate.path, window.location.origin);
        url.searchParams.set(PARAM, model);
        const href = url.pathname + url.search;
        url.searchParams.set('section_id', sectionId);
        try {
          const response = await fetch(url.toString());
          if (!response.ok) return null;
          const html = await response.text();
          return html.includes('nudient-collection-grid__item') ? { ...candidate, href } : null;
        } catch {
          return null;
        }
      })
    );
    const found = checked.filter(Boolean);
    if (!found.length || !this.isConnected) return;

    const links = found.map((item) => {
      const anchor = document.createElement('a');
      anchor.className = 'nudient-filter-empty__type';
      anchor.href = item.href;
      anchor.textContent = item.name;
      return anchor;
    });
    const fill = (template) => template.replaceAll('[type]', type).replaceAll('[model]', model);
    const nodes = [document.createTextNode(fill(lead) + gap)];
    const [before, after = ''] = fill(see).split('[types]');
    nodes.push(document.createTextNode(before));
    let parts;
    try {
      parts = new Intl.ListFormat(document.documentElement.lang || undefined, { type: 'disjunction' }).formatToParts(found.map((item) => item.name));
    } catch {
      parts = found.flatMap((item, i) => (i ? [{ type: 'literal', value: ', ' }, { type: 'element', value: item.name }] : [{ type: 'element', value: item.name }]));
    }
    let index = 0;
    for (const part of parts) nodes.push(part.type === 'element' ? links[index++] : document.createTextNode(part.value));
    nodes.push(document.createTextNode(after));
    text.replaceChildren(...nodes);
  }
}

if (!customElements.get('nudient-empty-types')) customElements.define('nudient-empty-types', NudientEmptyTypes);
